import { ApiPaths } from 'api/constants';
import {
  GridTemplateGeneral,
  GridType,
  TemplateLayout,
} from 'api/endpoints/grid';
import { ProcessGeneral } from 'api/endpoints/processes';
import ApiUrlParams from 'constants/apiUrlParams';
import { buildBadRequestApiError } from 'container/helpers/errors';

import BaseRepo from '../../BaseRepo';
import { ApiResponseAsync, isApiError } from '../../models';
import {
  GetTemplateResult,
  IGridRepo,
  SaveDefaultTemplateRequest,
  SaveDefaultTemplateResponse,
  SaveTemplateRequest,
} from './IGridRepo';

export class GridRepo extends BaseRepo implements IGridRepo {
  removeTemplate = (
    template: GridTemplateGeneral,
    type: GridType
  ): ApiResponseAsync<void> => {
    const { REACT_APP_API_BASE: baseURL } = import.meta.env;
    const uri = new URL(
      baseURL +
        ApiPaths.gridTemplates.byId._({
          [ApiUrlParams.templateId]: template.id,
        })
    );
    uri.searchParams.append('type', type);
    uri.searchParams.append('process_id', template.processId);

    return this.helpers.handleDelete(uri.toString());
  };
  activateTemplate = (
    template: GridTemplateGeneral,
    type: GridType
  ): ApiResponseAsync<GridTemplateGeneral> => {
    const { REACT_APP_API_BASE: baseURL } = import.meta.env;
    const uri = new URL(
      baseURL +
        ApiPaths.gridTemplates.byId.activate._({
          [ApiUrlParams.templateId]: template.id,
        })
    );
    uri.searchParams.append('type', type);
    uri.searchParams.append('process_id', template.processId);

    return this.helpers.handlePost(uri.toString(), template);
  };
  saveDefaultTemplate = (
    draft: ProcessGeneral,
    template: TemplateLayout,
    type: GridType,
    templates: GetTemplateResult
  ): ApiResponseAsync<SaveDefaultTemplateResponse> => {
    const link = templates._links
      ? templates._links['updateDefaultTemplate']
      : undefined;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "update default template" is not available')
        )
      );
    }

    const payload: SaveDefaultTemplateRequest = {
      processId: draft.id,
      layout: template,
      type,
    };

    return this.helpers.handlePut(link.href, payload, {
      requestStopTransformPaths: ['layout'],
    });
  };
  saveTemplate = (
    draft: ProcessGeneral,
    template: TemplateLayout,
    name: string,
    type: GridType
  ): ApiResponseAsync<GridTemplateGeneral> => {
    const request: SaveTemplateRequest = {
      layout: template,
      name,
      type,
      processId: draft.id,
    };

    return this.helpers.handlePost(ApiPaths.gridTemplates._(), request, {
      requestStopTransformPaths: ['layout'],
    });
  };
  getTemplates = (
    draft: ProcessGeneral,
    type: GridType
  ): ApiResponseAsync<GetTemplateResult> => {
    const { REACT_APP_API_BASE: baseURL } = import.meta.env;
    const uri = new URL(baseURL + ApiPaths.gridTemplates._());
    uri.searchParams.append('type', type);
    uri.searchParams.append('process_id', draft.id);

    return this.helpers.handleGet<GetTemplateResult>(uri.toString());
  };

  getGridActiveTemplate = (
    draft: ProcessGeneral,
    type: GridType
  ): ApiResponseAsync<Nullable<GridTemplateGeneral>> => {
    const { REACT_APP_API_BASE: baseURL } = import.meta.env;
    const uri = new URL(baseURL + ApiPaths.gridTemplates._());
    uri.searchParams.append('type', type);
    uri.searchParams.append('process_id', draft.id);

    return this.helpers
      .handleGet<GetTemplateResult>(uri.toString())
      .then((searchResult) => {
        if (isApiError(searchResult)) {
          return searchResult;
        }
        if (searchResult.data === null) {
          return null;
        }
        return (
          searchResult.data.find(
            (gridTemplate) => gridTemplate.status === 'active'
          ) ?? null
        );
      });
  };
}
